import React from 'react';
import { PrinterIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'; // Import icons

// Temporary Button, LoadingSpinner, ErrorMessage components (move to common/ui later)
const Button = ({ children, onClick, type = 'button', disabled, variant = 'primary', isLoading, className = '', icon: Icon }) => {
  const baseStyle = "py-2.5 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 inline-flex items-center justify-center";
  let variantStyle = '';
  switch (variant) {
    case 'secondary':
      variantStyle = "text-brand-teal bg-teal-100 hover:bg-teal-200 focus:ring-brand-teal";
      break;
    case 'danger':
      variantStyle = "text-white bg-red-600 hover:bg-red-700 focus:ring-red-500";
      break;
    case 'primary':
    default:
      variantStyle = "text-white bg-brand-teal hover:bg-brand-teal-dark focus:ring-brand-teal";
      break;
  }
  const disabledStyle = "opacity-50 cursor-not-allowed";

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`${baseStyle} ${variantStyle} ${disabled || isLoading ? disabledStyle : ''} ${className}`}
    >
      {isLoading ? <LoadingSpinner /> : (
        <>
          {Icon && <Icon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />}
          {children}
        </>
      )}
    </button>
  );
};

const LoadingSpinner = () => (
  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);
const ErrorMessage = ({ message }) => <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">{message}</div>;

const GenerateLabelAction = ({
  onGenerate,
  isLoading,
  isButtonDisabled,
  errorData, // from useCreateShippingLabelMutation
  successData, // from useCreateShippingLabelMutation
  history // from useHistory hook
}) => {
  return (
    <div className="mt-6 border-t border-gray-200 pt-6">
      {successData && successData.success ? (
        // Success Message
        <div className="p-4 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Label Generated Successfully!</h3>
          
          {successData.trackingNumber && <p className="mt-1 text-sm text-gray-600">Tracking Number: <span className="font-medium text-gray-700">{successData.trackingNumber}</span></p>}
          {successData.carrier && <p className="text-sm text-gray-600">Carrier: <span className="font-medium text-gray-700">{successData.carrier}</span></p>}
          
          {successData.labelUrl && 
            <div className="mt-6 mb-4">
              <Button 
                onClick={() => window.open(successData.labelUrl, '_blank')}
                variant="primary"
                className="w-full md:w-auto text-base px-8 py-3"
                icon={ArrowDownTrayIcon} // Added icon
              >
                View / Download Shipping Label
              </Button>
            </div>
          }

          <div className="mt-6 text-left text-sm text-gray-700 bg-gray-50 p-4 rounded-md">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
              <PrinterIcon className="h-5 w-5 mr-2 text-gray-500" />
              Next Steps:
            </h4>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Print the shipping label. Ensure it is clear and scannable.</li>
              <li>Securely attach the label to your package. Avoid placing tape over barcodes if possible.</li>
              <li>Drop off the package at the nearest <span className="font-medium">{successData.carrier || 'carrier'}</span> location.</li>
              <li>Alternatively, some carriers (like UPS, FedEx, etc.) may offer in-store QR code scanning or label printing services if you don't have a printer. Please check with <span className="font-medium">{successData.carrier || 'your carrier'}</span> for available options.</li>
            </ul>
          </div>

          <div className="mt-8">
             <Button onClick={() => history.push('/dashboard/transactions')} variant="secondary">Back to Merchant Dashboard</Button>
          </div>
        </div>
      ) : (
        // Generate Button and Errors
        <div className="text-center">
          {errorData && 
            <ErrorMessage 
              message={errorData.data?.message || errorData.data?.error?.message || errorData.error || 'Error creating label.'}
            />
          }
          <Button 
             onClick={onGenerate} 
             disabled={isButtonDisabled} // Pass combined disabled state
             isLoading={isLoading}
             className="w-full md:w-auto text-base px-8 py-3"
          >
             Generate Shipping Label
          </Button>
           {/* Specific warning for missing origin address moved to main page */}
        </div>
      )}
    </div>
  );
};

export default GenerateLabelAction; 